import { useEffect } from "react";
import { Localized, useLocalization } from "@fluent/react";
import { entities, modules, useFeature } from "byzantine";
import { useNavigate } from "react-router-dom";
import { Alert, Button, ContentCard, Row } from "@narmi/design_system";
import { DateTime } from "luxon";
import receiptStyles from "../ach/ReceiptScreen.module.scss";
import {
  formatFinancialInstitutionValue,
  getFromAccountValue,
  moneyFormatter,
} from "./TransferReview";
import { formatFrequencyValue } from "../FrequencySelector";
import { TRANSFERS_ROUTE } from "./TransferRoutes";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import styles from "./TransferReview.module.scss";

const ReceiptRow = ({
  name,
  value,
  pad = true,
}: {
  name: string;
  value: React.ReactNode;
  pad?: boolean;
}) => (
  <Row>
    <Row.Item shrink>
      <p className={pad ? "padding--top--s" : ""}>{name}</p>
    </Row.Item>
    <Row.Item>
      <p
        className={pad ? "padding--top--s" : ""}
        style={{
          textAlign: "right",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {value || "-"}
      </p>
    </Row.Item>
  </Row>
);

export const truncateStr = (s: string | undefined, maxLength = 20) =>
  s && s.length > maxLength ? `${s.substring(0, maxLength)}...` : s;

export default function TransferReceipt() {
  const form = modules.transfers.TransferForm.useForm();
  const { resetForm } = form;
  const resetTransfer = modules.transfers.useResetTransfer();
  const navigate = useNavigate();
  const { FedNowPaymentSuccessStates } = entities.transfers;
  const sourceAccount = modules.transfers.useSelectedSourceAccount();
  const destinationAccount = modules.transfers.useSelectedDestinationAccount();
  const paymentId = modules.transfers.useSubmittedTransferId();
  const transferState = modules.transfers.useSubmittedTransferState();
  const fee = modules.transfers.useCalculateInstantTransferFee();
  const fednowPayment = modules.transfers.useFedNowPayment();
  const isExternalTransfer = modules.transfers.useIsExternalTransfer();
  const { featureEnabled } = useFeature();
  const { l10n } = useLocalization();
  const institution = useInstitutionSettings();
  const transferSuccess =
    transferState === "processed" &&
    (fednowPayment
      ? FedNowPaymentSuccessStates.includes(fednowPayment.state)
      : true);
  const icon = transferSuccess ? "check" : "x";
  const bgColor = transferSuccess ? "success" : "error";
  const title = transferSuccess
    ? l10n.getString("header-transfer-receipt")
    : l10n.getString("header-transfer-rejected");
  const speed =
    !isExternalTransfer || form.values.instant
      ? l10n.getString("label-instant")
      : l10n.getString("label-ach-speed-short");
  const institutionName = institution.short_name || institution.long_name || "";
  const supportPhoneNumber = institution.help_phone || "";
  const shouldShowPullAuthorizationDetails = Boolean(
    transferSuccess &&
      featureEnabled("ach_pull_authorization_language") &&
      sourceAccount?.isExternal() &&
      !form.values.instant
  );
  let scheduledDate: string | undefined;

  if (form.values.instant) {
    scheduledDate = DateTime.now().toFormat("M/d/yyyy");
  } else if (form.values.date) {
    scheduledDate = DateTime.fromISO(form.values.date).toFormat("M/d/yyyy");
  }

  useEffect(() => {
    form.submitForm().then(({ success }) => {
      if (!success) {
        navigate(`/transfer/${TRANSFERS_ROUTE}`);
      }
    });
  }, []);

  return (
    <div className={receiptStyles.receiptContainer}>
      <ContentCard>
        <div className={receiptStyles.cardContainer}>
          <h2 className="padding--y">
            {title} <span className={`narmi-icon-${icon}`} />
          </h2>
          {!transferSuccess && (
            <Alert isActive isDismissable={false} kind="error">
              {paymentId // the existence of a paymentId indicates the payment was created but rejected by the FI. Missing paymentId indicates
                ? // monitoring blocked the payment before it could be posted.
                  l10n.getString("label-transfer-rejected-alert")
                : l10n.getString("label-transfer-declined-alert")}
            </Alert>
          )}
          <div className={`${styles.contentContainer} bgColor--${bgColor}`}>
            <ReceiptRow
              name={l10n.getString("label-from-account")}
              value={
                getFromAccountValue({
                  shouldShowPullAuthorizationDetails,
                  sourceAccount,
                  l10n,
                }) ?? truncateStr(sourceAccount?.getShortDescription(), 35)
              }
              pad={false}
            />
            {shouldShowPullAuthorizationDetails && (
              <ReceiptRow
                name={l10n.getString("label-from-financial-institution")}
                value={formatFinancialInstitutionValue(
                  sourceAccount?.fi_name,
                  sourceAccount?.routing
                )}
              />
            )}
            <ReceiptRow
              name={
                shouldShowPullAuthorizationDetails
                  ? l10n.getString("label-to-account")
                  : l10n.getString("label-deposit-to")
              }
              value={truncateStr(destinationAccount?.getShortDescription(), 35)}
            />
            <ReceiptRow
              name={l10n.getString("label-amount")}
              value={moneyFormatter.format(form.values.amount / 100)}
            />
            {shouldShowPullAuthorizationDetails && (
              <ReceiptRow
                name={l10n.getString("label-frequency")}
                value={formatFrequencyValue(form.values.frequency, l10n)}
              />
            )}
            <ReceiptRow
              name={l10n.getString("label-delivery-speed")}
              value={
                !transferSuccess
                  ? l10n.getString("label-transfer-rejected")
                  : speed
              }
            />
            <ReceiptRow
              name={l10n.getString("label-scheduled-date")}
              value={scheduledDate}
            />
            {form.values.instant && (
              <ReceiptRow
                name={l10n.getString("label-fee")}
                value={
                  fee === 0
                    ? l10n.getString("label-no-fee")
                    : moneyFormatter.format(fee)
                }
              />
            )}

            {paymentId && !shouldShowPullAuthorizationDetails && (
              <ReceiptRow
                name={l10n.getString("label-payment-id")}
                value={truncateStr(paymentId, 35)}
              />
            )}
          </div>
          {shouldShowPullAuthorizationDetails && (
            <Localized
              id="ach-payment-pull-authorization-disclosure"
              vars={{ institutionName, supportPhoneNumber }}
            >
              <div className={styles.disclosureText} />
            </Localized>
          )}
          <div className={receiptStyles.buttonContainer}>
            {shouldShowPullAuthorizationDetails && (
              <Button
                className={receiptStyles.printButton}
                size="m"
                startIcon="printer"
                label={l10n.getString("label-print")}
                kind="plain"
                onClick={() => {
                  window.print();
                }}
              />
            )}
            <div className={styles.actionButtons}>
              <Button
                label={l10n.getString("label-make-another-transfer")}
                testId="make-another-transfer"
                kind="tertiary"
                onClick={() => {
                  resetTransfer();
                  resetForm();
                  navigate(`/transfer/${TRANSFERS_ROUTE}`);
                }}
              />
              <Button
                label={l10n.getString("label-done")}
                testId="transferreceipt-done"
                onClick={() => {
                  window.location.href = "/";
                }}
              />
            </div>
          </div>
        </div>
      </ContentCard>
    </div>
  );
}
